import { useCallback, useEffect, useMemo, useState } from "react";
import type { Hasher, Kennel, Membership } from "@hash/shared";
import { membershipState, membershipMissingFields } from "@hash/shared";
import { api } from "../api/client";
import { getClaims } from "../auth";
import { parseApiError } from "../api/errors";
import { todayLocalISO } from "../lib/checkInStatus";
import { formatCalendarDate } from "../lib/trailDate";
import { RecordDuesModal } from "../components/RecordDuesModal";
import { PhoneDialog } from "../components/PhoneDialog";
import { AccountStateBadge, MismanBadge } from "../components/HasherBadges";
import { addHasher, removeHasher } from "../stores/hasherStore";

interface Data {
  hasher: Hasher;
  memberships: Membership[];
}

// What the dues modal is open for: a specific kennel (from a membership row) or
// "any" (pick a managed kennel), or null when closed.
type DuesTarget = { id: string; name: string } | "any" | null;

/**
 * The staff-facing hasher Manage screen. Reached by tapping a hasher in the
 * roster, it folds everything you can do to a hasher into one place: identity
 * edits (name / home kennel / visitor), phone assignment, membership state +
 * recording dues, the misman roles they hold, and (admin) deletion. It replaced
 * the old split between a row-tap detail page and a separate edit modal.
 */
export function HasherManagePage({
  hasherId,
  onBack,
}: {
  hasherId: string;
  onBack: () => void;
}) {
  const [data, setData] = useState<Data | null>(null);
  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dues, setDues] = useState<DuesTarget>(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  // Kennels (by id) this hasher holds a misman role on, aggregated from the
  // managed kennels' rosters.
  const [mismanKennelIds, setMismanKennelIds] = useState<Set<string>>(
    new Set()
  );

  const today = todayLocalISO();
  const kennelName = (id: string) =>
    kennels.find((k) => k.id === id)?.name ?? id;
  // Editing is gated to what the caller can do server-side: admins do anything;
  // a misman manages dues + misman roles only for their own kennels.
  const claims = getClaims();
  const isAdmin = claims?.role === "admin";
  const manages = (id: string) =>
    isAdmin || (claims?.mismanKennels ?? []).includes(id);
  const managedKennels = kennels.filter((k) => k.managed && manages(k.id));

  const load = useCallback(() => {
    setError(null);
    api.hashers
      .memberships(hasherId)
      .then(setData)
      .catch((e) => setError(parseApiError(e).message));
  }, [hasherId]);

  useEffect(() => load(), [load]);

  // Kennel names + the managed list (dues picker), and the hasher's misman
  // roles — aggregated from each managed kennel's roster (misman only exist on
  // managed kennels). Paged like the roster's kennel filter; the list is small.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels);
        next = res.cursor;
      } while (next);
      if (cancelled) return;
      setKennels(collected);

      const rosters = await Promise.all(
        collected
          .filter((k) => k.managed)
          .map((k) =>
            api.kennels
              .listMisman(k.id)
              .then((r) => r.misman)
              .catch(() => [])
          )
      );
      if (cancelled) return;
      const mine = new Set<string>();
      for (const a of rosters.flat()) {
        if (a.hasherId === hasherId) mine.add(a.kennelId);
      }
      setMismanKennelIds(mine);
    })().catch((e) => console.error("Failed to load kennels/misman", e));
    return () => {
      cancelled = true;
    };
  }, [hasherId]);

  // Reflect a hasher mutation in both the local view and the shared roster store
  // so the list is up to date when we navigate back.
  const applyHasher = (h: Hasher) => {
    setData((d) => (d ? { ...d, hasher: h } : d));
    addHasher(h);
  };

  if (error) return <p className="px-4 py-6 text-sm text-red-600">{error}</p>;
  if (!data) return <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>;

  const { hasher, memberships } = data;
  const isMisman = mismanKennelIds.size > 0;
  // A nerd name and email are required before dues can be recorded (the server
  // enforces this on record; we mirror it so the affordance isn't a dead end).
  const missing = membershipMissingFields(hasher);
  const canRecordDues = missing.length === 0;
  // Memberships sorted by kennel name for a stable display.
  const sorted = [...memberships].sort((a, b) =>
    kennelName(a.kennelId).localeCompare(kennelName(b.kennelId))
  );

  return (
    <div className="min-h-screen px-4 py-6 pb-24">
      <button
        onClick={onBack}
        className="mb-3 text-sm text-slate-500 hover:text-slate-900"
      >
        ← Hashers
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-bold text-slate-900">{hasher.name}</h2>
        <AccountStateBadge state={hasher.accountState} />
        {isMisman && <MismanBadge />}
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {hasher.homeKennel}
        {hasher.visitor && " · visitor"}
        {hasher.phone && ` · ${hasher.phone}`}
      </p>
      {hasher.displayName && (
        <p className="text-sm text-slate-500">
          Hares publicly as “{hasher.displayName}”
        </p>
      )}
      {(hasher.nerdName || hasher.email) && (
        <p className="text-sm text-slate-500">
          {[hasher.nerdName, hasher.email].filter(Boolean).join(" · ")}
        </p>
      )}
      {hasher.lastAttendedAt && (
        <p className="text-sm text-slate-500">
          Last hashed{" "}
          {formatCalendarDate(hasher.lastAttendedAt, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
      )}

      <IdentitySection
        hasher={hasher}
        kennels={kennels}
        onSaved={applyHasher}
      />

      <PhoneSection hasher={hasher} onOpen={() => setPhoneOpen(true)} />

      <section className="mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Memberships</h3>
          {managedKennels.length > 0 && (
            <button
              onClick={() => setDues("any")}
              disabled={!canRecordDues}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white active:bg-slate-700 disabled:opacity-50"
            >
              Record dues
            </button>
          )}
        </div>
        {managedKennels.length > 0 && !canRecordDues && (
          <p className="mt-2 text-xs text-amber-700">
            Add a {missing.join(" and ")} (in Details above) before recording
            dues.
          </p>
        )}
        <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {sorted.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-400">
              No memberships yet.
            </li>
          ) : (
            sorted.map((m) => {
              const state = membershipState(m, today);
              const through = m.periods
                .map((p) => p.end)
                .sort()
                .at(-1);
              return (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900">
                      {kennelName(m.kennelId)}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {state}
                      {through ? ` · through ${through}` : ""}
                    </span>
                  </span>
                  {manages(m.kennelId) && (
                    <button
                      onClick={() =>
                        setDues({
                          id: m.kennelId,
                          name: kennelName(m.kennelId),
                        })
                      }
                      disabled={!canRecordDues}
                      className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-transparent"
                    >
                      Record dues
                    </button>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </section>

      <MismanSection
        hasher={hasher}
        kennels={kennels}
        mismanKennelIds={mismanKennelIds}
        manages={manages}
        onChange={setMismanKennelIds}
      />

      {isAdmin && (
        <DeleteSection
          hasher={hasher}
          onDeleted={() => {
            removeHasher(hasher.id);
            onBack();
          }}
        />
      )}

      {dues && (
        <RecordDuesModal
          hasher={hasher}
          fixedKennel={dues === "any" ? undefined : dues}
          managedKennels={dues === "any" ? managedKennels : undefined}
          onClose={() => setDues(null)}
          onRecorded={() => {
            setDues(null);
            load(); // reflect the new period in the membership state
          }}
        />
      )}

      {phoneOpen && (
        <PhoneDialog
          title={hasher.phone ? "Fix phone number" : "Add phone number"}
          hint={
            hasher.phone
              ? `Replace the number on ${hasher.name}'s unclaimed account. No code is sent.`
              : `Vouch for ${hasher.name} so they can log in. No code is sent.`
          }
          submitLabel={hasher.phone ? "Update" : "Attach"}
          onClose={() => setPhoneOpen(false)}
          onSubmit={async (e164) => {
            const res = await api.hashers.setPhone(hasher.id, e164);
            applyHasher({
              ...hasher,
              phone: res.phone,
              accountState: res.accountState,
            });
            setPhoneOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** Inline editor for name / home kennel / visitor (formerly the edit modal). */
function IdentitySection({
  hasher,
  kennels,
  onSaved,
}: {
  hasher: Hasher;
  kennels: Kennel[];
  onSaved: (h: Hasher) => void;
}) {
  const [name, setName] = useState(hasher.name);
  const [displayName, setDisplayName] = useState(hasher.displayName ?? "");
  const [nerdName, setNerdName] = useState(hasher.nerdName ?? "");
  const [email, setEmail] = useState(hasher.email ?? "");
  const [homeKennelId, setHomeKennelId] = useState(hasher.homeKennelId);
  const [visitor, setVisitor] = useState(hasher.visitor);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    name.trim() !== hasher.name ||
    displayName.trim() !== (hasher.displayName ?? "") ||
    nerdName.trim() !== (hasher.nerdName ?? "") ||
    email.trim() !== (hasher.email ?? "") ||
    homeKennelId !== hasher.homeKennelId ||
    visitor !== hasher.visitor;

  const kennelOptions = useMemo(
    () =>
      [...kennels].sort((a, b) => {
        if (a.managed !== b.managed) return b.managed ? 1 : -1;
        return a.name.localeCompare(b.name);
      }),
    [kennels]
  );

  const save = async () => {
    if (!name.trim()) {
      setErr("Name is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.hashers.update(hasher.id, {
        name: name.trim(),
        // Blank clears the field server-side; send the trimmed value either way.
        displayName: displayName.trim(),
        nerdName: nerdName.trim(),
        email: email.trim(),
        homeKennelId,
        visitor,
      });
      onSaved(updated);
    } catch (e) {
      setErr(parseApiError(e, "Failed to save").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-slate-700">Details</h3>
      <div className="mt-2 space-y-3 rounded-lg border border-slate-200 bg-white p-3">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Hash name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Hash name"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Display name{" "}
            <span className="font-normal normal-case">(optional)</span>
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            aria-label="Display name"
            placeholder="Shown on the public hareline instead of the hash name"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Nerd name{" "}
            <span className="font-normal normal-case">(optional)</span>
          </span>
          <input
            value={nerdName}
            onChange={(e) => setNerdName(e.target.value)}
            aria-label="Nerd name"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Email <span className="font-normal normal-case">(optional)</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Home kennel
          </span>
          <select
            value={homeKennelId}
            onChange={(e) => setHomeKennelId(e.target.value)}
            aria-label="Home kennel"
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            {kennelOptions.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          role="switch"
          aria-checked={visitor}
          aria-label="Visitor"
          onClick={() => setVisitor((v) => !v)}
          className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3"
        >
          <span className="font-medium text-slate-900">Visitor</span>
          <span
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              visitor ? "bg-emerald-600" : "bg-slate-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                visitor ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </span>
        </button>
        {err && (
          <p className="text-sm text-red-600" role="alert">
            {err}
          </p>
        )}
        <button
          onClick={save}
          disabled={busy || !dirty}
          className="h-12 w-full rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save details"}
        </button>
      </div>
    </section>
  );
}

/** Phone display + the add/fix control (hidden once the account is claimed). */
function PhoneSection({
  hasher,
  onOpen,
}: {
  hasher: Hasher;
  onOpen: () => void;
}) {
  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-slate-700">Phone</h3>
      <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3">
        <span className="min-w-0 text-sm">
          {hasher.phone ? (
            <span className="font-medium text-slate-900">{hasher.phone}</span>
          ) : (
            <span className="text-slate-400">No phone yet</span>
          )}
          {hasher.accountState === "claimed" && (
            <span className="block text-xs text-slate-500">
              Claimed — only they or an admin can change it.
            </span>
          )}
        </span>
        {hasher.accountState !== "claimed" && (
          <button
            onClick={onOpen}
            aria-label={
              hasher.phone
                ? `Fix phone for ${hasher.name}`
                : `Add phone for ${hasher.name}`
            }
            className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {hasher.phone ? "Fix phone" : "Add phone"}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * The misman roles this hasher holds, with grant/revoke. A control is offered
 * only for kennels the caller manages (admin ∨ misman of that kennel), matching
 * the server gate; granting requires a claimed account.
 */
function MismanSection({
  hasher,
  kennels,
  mismanKennelIds,
  manages,
  onChange,
}: {
  hasher: Hasher;
  kennels: Kennel[];
  mismanKennelIds: Set<string>;
  manages: (kennelId: string) => boolean;
  onChange: (next: Set<string>) => void;
}) {
  const [grantKennelId, setGrantKennelId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const kennelName = (id: string) =>
    kennels.find((k) => k.id === id)?.name ?? id;
  const current = [...mismanKennelIds].sort((a, b) =>
    kennelName(a).localeCompare(kennelName(b))
  );
  // Managed kennels the caller can grant on that the hasher isn't already a
  // misman of.
  const grantable = kennels
    .filter((k) => k.managed && manages(k.id) && !mismanKennelIds.has(k.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const claimed = hasher.accountState === "claimed";

  const grant = async (kennelId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await api.kennels.grantMisman(kennelId, hasher.id);
      const next = new Set(mismanKennelIds);
      next.add(kennelId);
      onChange(next);
      setGrantKennelId("");
    } catch (e) {
      setErr(parseApiError(e, "Failed to grant misman").message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (kennelId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await api.kennels.revokeMisman(kennelId, hasher.id);
      const next = new Set(mismanKennelIds);
      next.delete(kennelId);
      onChange(next);
    } catch (e) {
      setErr(parseApiError(e, "Failed to revoke misman").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-slate-700">Misman</h3>
      <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {current.length === 0 ? (
          <li className="px-3 py-2 text-sm text-slate-400">
            Not a misman of any kennel.
          </li>
        ) : (
          current.map((kid) => (
            <li
              key={kid}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="min-w-0 font-medium text-slate-900">
                {kennelName(kid)}
              </span>
              {manages(kid) && (
                <button
                  onClick={() => revoke(kid)}
                  disabled={busy}
                  aria-label={`Remove misman of ${kennelName(kid)}`}
                  className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </li>
          ))
        )}
      </ul>

      {grantable.length > 0 && (
        <div className="mt-2 flex gap-2">
          <select
            value={grantKennelId}
            onChange={(e) => setGrantKennelId(e.target.value)}
            aria-label="Grant misman of kennel"
            className="h-11 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            <option value="">Grant misman of…</option>
            {grantable.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => grant(grantKennelId)}
            disabled={busy || !grantKennelId || !claimed}
            className="h-11 shrink-0 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            Grant
          </button>
        </div>
      )}
      {grantable.length > 0 && !claimed && (
        <p className="mt-1 text-xs text-slate-500">
          {hasher.name} must claim their account (log in once) before they can
          be misman.
        </p>
      )}
      {err && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}
    </section>
  );
}

/** Admin-only destructive delete, kept at the bottom of the manage screen. */
function DeleteSection({
  hasher,
  onDeleted,
}: {
  hasher: Hasher;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const remove = async () => {
    if (!confirm(`Delete ${hasher.name}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.hashers.delete(hasher.id);
      onDeleted();
    } catch (e) {
      setErr(parseApiError(e, "Failed to delete hasher").message);
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 border-t border-slate-200 pt-6">
      <button
        onClick={remove}
        disabled={busy}
        className="h-12 w-full rounded-lg border border-red-200 font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
      >
        {busy ? "Deleting…" : `Delete ${hasher.name}`}
      </button>
      {err && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {err}
        </p>
      )}
    </section>
  );
}
